/**
 * Signal Admin API Client
 *
 * API client for the gw-signals-api: signal definition registry management
 * and per-entity signal evaluation/rescoring.
 *
 * Base URL: signals.{env}.generalwisdom.com
 * Auth: Bearer JWT with global_admin role claim (required on all endpoints)
 * Part of GW-4500 Declarative Signal Engine.
 */

import { getConfig } from '@/config/runtimeConfig';

import { BaseApiClient } from './baseApiClient';

// ============================================================================
// Types — aligned with gw-signals-api OpenAPI spec (signals.yaml)
// ============================================================================

export interface SignalDefinitionSummary {
  name: string;
  category: string;
  weight: number;
  evaluationInterval: string;
  evaluationMode: 'realtime' | 'batch' | 'on-demand';
  appliesTo: ('user' | 'organization')[];
  version: string;
  deployedAt: string;
  deployedBy: string;
  enabled: boolean;
}

export interface SignalDefinition extends SignalDefinitionSummary {
  /** Full JavaScript source. Only returned by GET /signal-definitions/{name}. */
  source?: string;
}

interface SignalRecord {
  name: string;
  category: string;
  score: number;
  status: 'resolved' | 'failed' | 'skipped';
  metadata: string;
  evaluatedAt: string;
  expiresAt: string;
  definitionVersion: string;
  weight: number;
}

export interface EvaluateSignalResponse {
  signalRecord: SignalRecord;
  fromCache: boolean;
  aggregateScore: number;
}

export interface RescoreResponse {
  entityId: string;
  score: number;
  riskLevel: string;
  signals: SignalRecord[];
  signalsFromCache: string[];
  signalsEvaluated: string[];
  signalsSkipped: string[];
}

/**
 * Response from GET /v1/trust-scores/{userId}
 * User-facing endpoint — requires Cognito JWT, no admin role required.
 */
export interface TrustScoreResponse {
  entityId: string;
  score: number;
  signals: Record<string, SignalRecord>;
  earnedGrants: string[];
  evaluatedAt: string;
}

interface GrantCondition {
  signalName: string;
  operator: 'eq' | 'gte' | 'lte' | 'gt' | 'lt';
  threshold: number;
}

export interface GrantDefinition {
  name: string;
  credentialLevel: string;
  description: string;
  appliesToEntity: string[];
  conditions: GrantCondition[];
  enabled: boolean;
  tokenCost: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface GrantDefinitionsResponse {
  grantDefinitions: GrantDefinition[];
}

export interface ComputeGrantsResponse {
  entityId: string;
  earnedGrants: string[];
  evaluatedAt: string;
}

/**
 * Response from GET /v1/grants/{entityId}
 * Any authenticated user may call this endpoint — global_admin is not required.
 */
interface GrantsResponse {
  entityId: string;
  grants: string[];
}

// Internal list response shape from GET /signal-definitions
interface SignalDefinitionListResponse {
  definitions: SignalDefinitionSummary[];
}

// ============================================================================
// API Client
// ============================================================================

// GW-8532: In-flight request dedup for getGrants. HeaderHost (global header,
// mounted once per app shell) and SettingsPage (settings tab shell) each
// independently fetch grants for the current user on mount, with no
// coordination between them — firing the same GET /v1/grants/{userId} twice
// on any page load where both mount (org-audit GW-8532). Deduping here,
// keyed by entityId, means every caller shares one in-flight request instead
// of the client tracking down and coordinating each mount site.
let inFlightGrantsFetch: { entityId: string; promise: Promise<GrantsResponse> } | null = null;

class SignalsApiClient extends BaseApiClient {
  constructor() {
    // Signals endpoints live on the signals gateway (signals.{env}.generalwisdom.com),
    // separate from gw-functions. Falls back to apiBaseUrl when signalsApiBaseUrl
    // is not configured.
    // GW-6394: Use baseURLFactory to defer getConfig() until first API call.
    super({
      baseURLFactory: () => getConfig().signalsApiBaseUrl ?? getConfig().apiBaseUrl,
      suppressSessionExpired: true,
    });
  }

  /**
   * List all signal definitions (source field excluded)
   * GET /signal-definitions
   * Authorization: global_admin role
   */
  async listSignalDefinitions(): Promise<SignalDefinitionSummary[]> {
    const response = await this.get<SignalDefinitionListResponse>('/v1/signal-definitions');
    return response.definitions;
  }

  /**
   * Get a single signal definition (includes source field)
   * GET /v1/signal-definitions/{name}
   * Authorization: global_admin role
   */
  async getSignalDefinition(name: string): Promise<SignalDefinition> {
    return this.get<SignalDefinition>(`/v1/signal-definitions/${name}`);
  }

  /**
   * Enable a signal definition
   * POST /v1/signal-definitions/{name}/enable
   * Authorization: global_admin role
   *
   * Returns the full SignalDefinition with enabled=true.
   * Effect is eventual within the Lambda cache-ttl window (default 5m).
   */
  async enableSignal(name: string): Promise<SignalDefinition> {
    return this.post<SignalDefinition>(`/v1/signal-definitions/${name}/enable`);
  }

  /**
   * Disable a signal definition
   * POST /v1/signal-definitions/{name}/disable
   * Authorization: global_admin role
   *
   * Returns the full SignalDefinition with enabled=false.
   * Existing SignalRecords expire naturally; they are not deleted.
   */
  async disableSignal(name: string): Promise<SignalDefinition> {
    return this.post<SignalDefinition>(`/v1/signal-definitions/${name}/disable`);
  }

  /**
   * Evaluate a single signal for an entity
   * POST /v1/signals/{entityId}/evaluate/{signalName}
   * Authorization: global_admin role
   *
   * Evaluation is idempotent within the evaluationInterval — a cached
   * SignalRecord is returned unchanged if ExpiresAt has not passed.
   */
  async evaluateSignal(entityId: string, signalName: string): Promise<EvaluateSignalResponse> {
    return this.post<EvaluateSignalResponse>(`/v1/signals/${entityId}/evaluate/${signalName}`);
  }

  /**
   * Rescore all signals for an entity
   * POST /v1/trust-scores/{entityId}/rescore
   * Authorization: global_admin role
   *
   * Evaluates all enabled signal definitions for the entity, honoring
   * idempotency — signals within their evaluationInterval are served from cache.
   */
  async rescoreEntity(entityId: string): Promise<RescoreResponse> {
    return this.post<RescoreResponse>(`/v1/trust-scores/${entityId}/rescore`);
  }

  /**
   * Get the current trust score for a user (user-facing endpoint)
   * GET /v1/trust-scores/{userId}
   * Authorization: Cognito JWT (any authenticated user, for their own userId)
   *
   * Returns aggregate score, per-signal breakdown, and earned grants.
   * Throws ApiError on 401 (redirect to login handled by BaseApiClient).
   */
  async getTrustScore(userId: string): Promise<TrustScoreResponse> {
    return this.get<TrustScoreResponse>(`/v1/trust-scores/${userId}`);
  }

  /**
   * List all grant definitions
   * GET /v1/grant-definitions
   * Authorization: global_admin role
   *
   * Returns grant definitions that map signal conditions to credential levels.
   */
  async getGrantDefinitions(): Promise<GrantDefinitionsResponse> {
    return this.get<GrantDefinitionsResponse>('/v1/grant-definitions');
  }

  /**
   * Compute earned grants for an entity
   * POST /v1/trust-scores/{entityId}/compute-grants
   * Authorization: global_admin role
   *
   * Evaluates all grant definitions against the entity's current signal scores
   * and returns the set of grants the entity has earned.
   */
  async computeGrants(entityId: string): Promise<ComputeGrantsResponse> {
    return this.post<ComputeGrantsResponse>(`/v1/trust-scores/${entityId}/compute-grants`);
  }

  /**
   * Get earned grants for an entity
   * GET /v1/grants/{entityId}
   * Authorization: any authenticated Cognito JWT (not global_admin restricted)
   *
   * Returns the stored grants for the entity from the trust-scores table.
   * Returns an empty array (not 404) when the entity exists but has no grants.
   *
   * GW-8532: Concurrent calls for the same entityId share one in-flight
   * request instead of each issuing a separate GET — see inFlightGrantsFetch
   * doc comment above.
   */
  async getGrants(entityId: string): Promise<GrantsResponse> {
    if (inFlightGrantsFetch && inFlightGrantsFetch.entityId === entityId) {
      return inFlightGrantsFetch.promise;
    }

    const promise = this.get<GrantsResponse>(`/v1/grants/${entityId}`).finally(() => {
      if (inFlightGrantsFetch?.entityId === entityId) {
        inFlightGrantsFetch = null;
      }
    });

    inFlightGrantsFetch = { entityId, promise };
    return promise;
  }
}

// Export singleton instance
export const signalsApi = new SignalsApiClient();
